import React from 'react';
import ReactECharts from 'echarts-for-react';
import { EVChargingStatus } from '../../types/energyData';
import { EnergyDataService } from '../../services/energyDataService';
import { useTranslation } from 'react-i18next';

interface EVChargingDetailViewProps {
  data: EVChargingStatus;
  energyDataService: EnergyDataService;
}

export const EVChargingDetailView: React.FC<EVChargingDetailViewProps> = ({ data, energyDataService }) => {
  const { t } = useTranslation();
  // Get REAL time series data from JSON files - NO FAKE DATA
  const evChargingTimeSeriesData = energyDataService.getEVChargingTimeSeriesData();
  
  // Use real data if available, otherwise show current values only
  const hasEVData = evChargingTimeSeriesData.ec1Data.length > 0;
  
  const timeData = hasEVData ? evChargingTimeSeriesData.timeLabels : ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'];
  
  // Use REAL data from JSON files - NO RANDOM VARIATIONS
  const ec1PowerData = hasEVData ? evChargingTimeSeriesData.ec1Data : [data.ec1.power, data.ec1.power, data.ec1.power, data.ec1.power, data.ec1.power, data.ec1.power, data.ec1.power];
  const ec2PowerData = hasEVData ? evChargingTimeSeriesData.ec2Data : [data.ec2.power, data.ec2.power, data.ec2.power, data.ec2.power, data.ec2.power, data.ec2.power, data.ec2.power];
  
  // Energy data should be based on actual power values
  const ec1EnergyData = ec1PowerData.map((power, i) => data.ec1.energy + (power * i * 0.25));

  const getChargerStatusColor = (state: string) => {
    switch (state) {
      case 'Charging': return '#10B981';
      case 'ReadyToCharge': return '#3B82F6';
      case 'Completed': return '#8B5CF6';
      case 'Disconnected': return '#6B7280';
      case 'Error': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getVehicleStateColor = (state: string) => {
    switch (state) {
      case 'Connected': return '#10B981';
      case 'Charging': return '#10B981';
      case 'Ready': return '#3B82F6';
      case 'Disconnected': return '#6B7280';
      default: return '#6B7280';
    }
  };

  const powerChartOption = {
    title: {
      text: t('evCharging.chargingPowerOverTime'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        let result = `${params[0].axisValue}<br/>`;
        params.forEach((param: any) => {
          result += `${param.seriesName}: ${param.value} kW<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: [t('evCharging.ec1Power'), t('evCharging.ec2Power')],
      top: 30
    },
    xAxis: {
      type: 'category',
      data: timeData
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} kW'
      }
    },
    series: [
      {
        name: t('evCharging.ec1Power'),
        data: ec1PowerData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#3B82F6' },
        areaStyle: { color: 'rgba(59, 130, 246, 0.2)' }
      },
      {
        name: t('evCharging.ec2Power'),
        data: ec2PowerData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#8B5CF6' },
        areaStyle: { color: 'rgba(139, 92, 246, 0.2)' }
      }
    ]
  };

  const energyChartOption = {
    title: {
      text: t('evCharging.energyDeliveredEC1'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: {c} kWh'
    },
    xAxis: {
      type: 'category',
      data: timeData
    },
    yAxis: {
      type: 'value',
      min: function(value: any) {
        return Math.max(0, value.min - 50);
      },
      max: function(value: any) {
        return value.max + 50;
      },
      axisLabel: {
        formatter: function(value: number) {
          return value.toFixed(0) + ' kWh';
        }
      }
    },
    series: [{
      data: ec1EnergyData,
      type: 'line',
      smooth: true,
      lineStyle: { color: '#10B981' },
      areaStyle: { color: 'rgba(16, 185, 129, 0.2)' }
    }]
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">🚗 {t('evCharging.evChargingManagement')}</h2>
            <p className="text-gray-600">{t('evCharging.evChargingMonitoring')}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">{(data.ec1.power + data.ec2.power).toFixed(1)} kW</div>
            <div className="text-sm text-gray-500">{t('evCharging.totalChargingPower')}</div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <ReactECharts option={powerChartOption} style={{ height: '300px' }} />
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <ReactECharts option={energyChartOption} style={{ height: '300px' }} />
        </div>
      </div>

      {/* Charger Status Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* EC1 Status */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🔌 {t('evCharging.chargerEC1')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('evCharging.chargerState')}</span>
              <span 
                className="px-3 py-1 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: getChargerStatusColor(data.ec1.state) }}
              >
                {data.ec1.state}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('evCharging.vehicleState')}</span>
              <span 
                className="px-3 py-1 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: getVehicleStateColor(data.ec1.vehicleState) }}
              >
                {data.ec1.vehicleState}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('evCharging.chargingPower')}</span>
              <span className="font-semibold">{data.ec1.power.toFixed(1)} kW</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('evCharging.energyDelivered')}</span>
              <span className="font-semibold">{data.ec1.energy.toFixed(1)} kWh</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('evCharging.phase1Current')}</span>
              <span className="font-semibold">{data.ec1.current.p1.toFixed(1)} A</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Phase 1 Voltage</span>
              <span className="font-semibold">{data.ec1.voltage.p1.toFixed(1)} V</span>
            </div>
          </div>
        </div>

        {/* EC2 Status */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🔌 {t('evCharging.chargerEC2')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('evCharging.chargerState')}</span>
              <span 
                className="px-3 py-1 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: getChargerStatusColor(data.ec2.state) }}
              >
                {data.ec2.state}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('evCharging.vehicleState')}</span>
              <span 
                className="px-3 py-1 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: getVehicleStateColor(data.ec2.vehicleState) }}
              >
                {data.ec2.vehicleState}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('evCharging.chargingPower')}</span>
              <span className="font-semibold">{data.ec2.power.toFixed(1)} kW</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('evCharging.energyDelivered')}</span>
              <span className="font-semibold">{data.ec2.energy.toFixed(1)} kWh</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('evCharging.phase1Current')}</span>
              <span className="font-semibold">{data.ec2.current.p1.toFixed(1)} A</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Phase 1 Voltage</span>
              <span className="font-semibold">{data.ec2.voltage.p1.toFixed(1)} V</span>
            </div>
          </div>
        </div>
      </div>

      {/* Phase Details */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">⚡ {t('evCharging.phaseMeasurements')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">{t('evCharging.ec13PhaseDetails')}</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('evCharging.phase1')}</span>
                <span>{data.ec1.current.p1.toFixed(1)}A / {data.ec1.voltage.p1.toFixed(1)}V</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('evCharging.phase2')}</span>
                <span>{data.ec1.current.p2.toFixed(1)}A / {data.ec1.voltage.p2.toFixed(1)}V</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('evCharging.phase3')}</span>
                <span>{data.ec1.current.p3.toFixed(1)}A / {data.ec1.voltage.p3.toFixed(1)}V</span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-3">{t('evCharging.ec23PhaseDetails')}</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('evCharging.phase1')}</span>
                <span>{data.ec2.current.p1.toFixed(1)}A / {data.ec2.voltage.p1.toFixed(1)}V</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('evCharging.phase2')}</span>
                <span>{data.ec2.current.p2.toFixed(1)}A / {data.ec2.voltage.p2.toFixed(1)}V</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('evCharging.phase3')}</span>
                <span>{data.ec2.current.p3.toFixed(1)}A / {data.ec2.voltage.p3.toFixed(1)}V</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charging Statistics */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Charging Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{(data.ec1.energy + data.ec2.energy).toFixed(1)}</div>
            <div className="text-sm text-gray-600">Total Energy (kWh)</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{(data.ec1.power + data.ec2.power).toFixed(1)}</div>
            <div className="text-sm text-gray-600">Active Power (kW)</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">2</div>
            <div className="text-sm text-gray-600">Chargers Available</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">22kW</div>
            <div className="text-sm text-gray-600">Max Power</div>
          </div>
        </div>
      </div>
    </div>
  );
};
